import { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard,
  BookOpen, 
  FileText, 
  FolderClosed,
  UserCog, 
  User, 
  Users, 
  Calendar,
  Settings,
  Mail,
  BarChart3,
  PanelLeftOpen,
  Tag,
  Linkedin,
  List
} from 'lucide-react';
import { useContentManager } from '@/hooks/useContentManager';
import { usePermissions } from '@/hooks/usePermissions';

export interface NavigationItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  category: string;
  requiredPermission?: string;
  children?: NavigationItem[];
  isExpanded?: boolean;
}

export const useNavigationItems = (userRole?: string) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { preloadView } = useContentManager();
  const { hasPermission } = usePermissions();
  
  // State to track expanded items for nested navigation
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  // Define all navigation items with nested structure
  const navigationItems = useMemo<NavigationItem[]>(() => [
    // Main category
    { 
      id: "dashboard", 
      label: "Dashboard", 
      icon: <LayoutDashboard size={20} />, 
      path: "/dashboard", 
      category: "Main" 
    },
    
    // Add unified Content Calendar at the top level
    {
      id: "content-calendar",
      label: "Content Calendar",
      icon: <Calendar size={20} />,
      path: "/dashboard/calendar",
      category: "Admin",
      requiredPermission: "view:blog",
    },
    
    // Admin Category - Blog section (remove calendar from children)
    { 
      id: "blog", 
      label: "Blog", 
      icon: <BookOpen size={20} />, 
      path: "/dashboard/blog", 
      category: "Admin", 
      requiredPermission: "view:blog",
      children: [
        { 
          id: "blog-posts", 
          label: "Posts", 
          icon: <FileText size={18} />, 
          path: "/dashboard/blog", 
          category: "Admin", 
          requiredPermission: "view:blog" 
        }
        // Calendar moved to top level
      ]
    },

    // LinkedIn section (remove calendar from children)
    { 
      id: "linkedin", 
      label: "LinkedIn", 
      icon: <Linkedin size={20} />, 
      path: "/dashboard/linkedin", 
      category: "Admin", 
      requiredPermission: "view:linkedin",
      children: [
        { 
          id: "linkedin-posts", 
          label: "Posts", 
          icon: <List size={18} />, 
          path: "/dashboard/linkedin", 
          category: "Admin", 
          requiredPermission: "view:linkedin" 
        }
        // Calendar moved to top level
      ]
    },
    
    // Admin Category - Newsletter section (unchanged)
    { 
      id: "newsletter", 
      label: "Newsletter", 
      icon: <Mail size={20} />, 
      path: "/dashboard/newsletter", 
      category: "Admin", 
      requiredPermission: "manage:newsletters",
      children: [
        { 
          id: "newsletter-subscribers", 
          label: "Subscribers", 
          icon: <Users size={18} />, 
          path: "/dashboard/newsletter", 
          category: "Admin", 
          requiredPermission: "manage:newsletters" 
        },
        { 
          id: "newsletter-templates", 
          label: "Templates", 
          icon: <PanelLeftOpen size={18} />, 
          path: "/dashboard/newsletter?tab=templates", 
          category: "Admin", 
          requiredPermission: "manage:newsletters" 
        },
        { 
          id: "newsletter-campaigns", 
          label: "Campaigns", 
          icon: <Tag size={18} />, 
          path: "/dashboard/newsletter?tab=campaigns", 
          category: "Admin", 
          requiredPermission: "manage:newsletters" 
        },
        { 
          id: "newsletter-analytics", 
          label: "Analytics", 
          icon: <BarChart3 size={18} />, 
          path: "/dashboard/newsletter/analytics", 
          category: "Admin", 
          requiredPermission: "manage:newsletters" 
        }
      ]
    },
    
    // Admin Category - Media Library
    { 
      id: "media", 
      label: "Media Library", 
      icon: <FolderClosed size={20} />, 
      path: "/dashboard/media", 
      category: "Admin",
      requiredPermission: "view:documents",
    },
    
    // Admin Category - Users section
    { 
      id: "users", 
      label: "Users", 
      icon: <UserCog size={20} />, 
      path: "/dashboard/users", 
      category: "Admin", 
      requiredPermission: "view:users",
    }
  ], []);

  // Group navigation items by category
  const categorizedItems = useMemo(() => {
    return navigationItems.reduce((acc, item) => {
      if (!acc[item.category]) {
        acc[item.category] = [];
      }
      acc[item.category].push(item);
      return acc;
    }, {} as Record<string, NavigationItem[]>);
  }, [navigationItems]);

  // Get all categories
  const categories = useMemo(() => {
    return Object.keys(categorizedItems);
  }, [categorizedItems]);

  // Filter items by permission
  const visibleItems = useMemo(() => {
    return navigationItems.filter(item => 
      !item.requiredPermission || hasPermission(item.requiredPermission)
    ).map(item => {
      if (item.children) {
        return {
          ...item,
          children: item.children.filter(child => 
            !child.requiredPermission || hasPermission(child.requiredPermission)
          )
        };
      }
      return item;
    });
  }, [navigationItems, hasPermission]);

  // Navigation handler
  const handleNavigate = (path: string) => {
    navigate(path);
  };

  // Preload handler
  const handlePreload = (path: string) => {
    preloadView(path);
  };

  // Toggle expanded state for an item
  const toggleExpanded = (id: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Check if an item or any of its children is active
  const isItemActive = (item: NavigationItem): boolean => {
    // For items with query parameters, just check the base path
    const currentPathBase = location.pathname;
    const itemPathBase = item.path.split('?')[0];
    
    if (currentPathBase === itemPathBase) {
      // For newsletter, check both path and tab parameter
      if (item.path.includes('?tab=')) {
        const tabParam = new URLSearchParams(location.search).get('tab');
        const itemTab = new URLSearchParams(item.path.split('?')[1]).get('tab');
        
        return tabParam === itemTab;
      }
      return true;
    }

    if (item.children) {
      return item.children.some(child => isItemActive(child));
    }

    return false;
  };

  // Check if an item is expanded
  const isItemExpanded = (id: string): boolean => {
    return !!expandedItems[id];
  };

  return {
    navigationItems,
    categorizedItems,
    categories,
    visibleItems,
    isActive: isItemActive,
    isExpanded: isItemExpanded,
    toggleExpanded,
    navigate: handleNavigate,
    preload: handlePreload
  };
};
